import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import SearchableDropdown from "../../../../../../../components/SearchableDropdown/SearchableDropdown";
import Button from "../../../../../../../components/Button/Button";
import CloseIcon from "../../../../../../../assets/svg/close-icon";
import { getExternalVendorOptions } from "../utils/constant";
import { RootState } from "../../../../../../../redux/store";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

interface WaitingOnThirdPartyModalProps {
  open: boolean;
  onSubmit: (externalVendor: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
  initialValue?: string | null;
}

const WaitingOnThirdPartyModal = ({
  open,
  onSubmit,
  onCancel,
  isLoading = false,
  initialValue = null,
}: WaitingOnThirdPartyModalProps) => {
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const clientCode = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region);

  const vendorOptions = useMemo(
    () =>
      getExternalVendorOptions(clientCode).map((vendor) => ({
        value: vendor,
        content: (
          <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
            {vendor}
          </span>
        ),
      })),
    [clientCode]
  );

  const handleCancel = () => {
    setSelectedVendor(null);
    onCancel();
  };

  const handleSubmit = () => {
    if (!selectedVendor) return;
    onSubmit(selectedVendor);
  };

  const handleBackdropClick = () => {
    if (!isLoading) handleCancel();
  };

  useEffect(() => {
    if (open) {
      setSelectedVendor(initialValue ?? null);
    }
  }, [open, initialValue]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 z-1000"
        onClick={handleBackdropClick}
      />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-1001 rounded-[8px] shadow-popper-blur-effect backdrop-blur-[20px] w-[420px] max-w-[90vw] max-h-[90vh] overflow-visible bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk">
        <div className="p-6">
          <div className="flex flex-col gap-9">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="text-heading-text dark:text-heading-text-dk font-medium text-lg">
                  Dependent properties
                </span>
                <button
                  onClick={handleCancel}
                  className="shrink-0 w-[15px] h-[15px] bg-gray-100 dark:bg-tag-gray-dk rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110 cursor-pointer"
                >
                  <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
                </button>
              </div>
              <p className="text-md font-normal text-data dark:text-data-dk">
                Your organisation has chosen to show these properties based on
                your choice for{" "}
                <span className="font-semibold">'{helpdeskLabels.ticketStatus}'</span>. Some
                properties may be required to continue.
              </p>
            </div>

            <div className="flex flex-col gap-3 w-[264px]">
              <div className="flex flex-col gap-1.5 ">
                <label className="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk">
                  {helpdeskLabels.ticketStatus}
                </label>
                <div className=" flex items-center h-[36px] px-2 rounded-lg border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-deposit dark:bg-deposit-dk text-sm font-semibold text-pure-white dark:text-pure-white ">
                    WAITING ON 3<sup className="text-[7px] mr-0.5">rd</sup>{" "}
                    PARTY
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk">
                  External Vendor
                </label>
                <SearchableDropdown
                  id="external-vendor-dropdown"
                  options={vendorOptions}
                  value={selectedVendor}
                  onChange={(value) => setSelectedVendor(value)}
                  placeholder="Select"
                  className="w-[264px]"
                  triggerClassName="!rounded-lg !h-[36px] !border-border-gray dark:!border-border-gray-dk"
                  selectedOptionContainerClassName="!px-2 !py-0 h-full"
                  dropdownClassName="!bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk !py-0"
                  dropdownMenuClassName="!max-h-[150px]"
                  optionClassName="!py-[8px]"
                  optionsListClassName="!py-0"
                  placeholderClassName="!text-md !text-gray-placeholder dark:!text-gray-placeholder-dk"
                  ariaLabel="External Vendor"
                  sectionHeading="External Vendor"
                  searchInputClassName="!rounded-t-[8px]"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                innerText="Cancel"
                onClick={handleCancel}
                disabled={isLoading}
                buttonContainerClass="min-w-[100px] min-h-[26px] py-[2px] px-2 text-md border border-border-gray dark:border-border-gray-dk bg-transparent dark:bg-transparent rounded-md cursor-pointer"
                innerTextClass="text-heading-text dark:text-heading-text-dk text-md font-semibold"
              />
              <Button
                innerText="Save"
                onClick={handleSubmit}
                disabled={isLoading || !selectedVendor}
                isLoading={isLoading}
                buttonContainerClass="disabled:opacity-30 min-w-[140px] min-h-[26px] py-[2px] px-12 text-md border border-verba-indigo dark:border-verba-indigo-dk bg-verba-indigo dark:bg-verba-indigo-dk rounded-md cursor-pointer"
                innerTextClass="text-pure-white dark:text-pure-white-dk text-md font-semibold"
                loaderColor="text-pure-white dark:text-pure-white-dk"
                loaderSize={15}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WaitingOnThirdPartyModal;
